import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles, useTheme } from '@mui/styles'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import UploadIcon from '@assets/images/icons/cloud_upload.svg'
import InfoIcon from '@assets/images/icons/info-icon.svg'
import CloseIcon from '@assets/images/icons/close2.svg'
import DropZoneUploader from '@/modules/base/components/file-uploader/drop-zone-uploader/drop-zone-uploader'
import lang from '@/lang'
import { USER_RIGHTS, ProductTypes } from '@/modules/base/util/constants'
import { checkAccessRights } from '@/modules'
import { ERROR_REASONS } from '@/modules/file/uploader'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { hideUploadHint } from '@/redux/ducks/receipt/edit'
import ThemeInterface from '@/theme-interface'
import ReceiptDocumentContainerAction from '../document-container-action/receipt-document-container-action'
import openStartTrialModal from '@/modules/base/components/modal/_helpers/openStartTrialModal'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'
// FLAG:useDocuAi — DMS→DocuAI dropzone (gated); flag-OFF keeps the legacy FineUploader DropZoneUploader.
// The drop zone itself lives INSIDE DocumentsUploadTrigger — see the JSX below.
import DocumentsUploadTrigger from '@/modules/document/components/upload/documents-upload-trigger'
import { RECEIPT_DMS_TRIGGER_ALLOWED_EXTENSIONS } from '@/modules/receipt/helpers/dms-receipt-upload'

const ALLOWED_FILE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'pdf', 'xml', 'ZUGFeRD']
const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: theme.white,
    padding: '20px 20px 30px 20px',
  },
  uploaderWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  header: {
    display: 'grid',
    paddingBottom: 20,
    gridTemplateAreas: '"empty info documentContainerButton"',
    gridTemplateColumns: '1fr auto 1fr',
    gap: 20,
    position: 'relative',
    top: -5,
  },
  info: {
    gridArea: 'info',
    display: 'flex',
    alignItems: 'center',
    background: '#e2e8fe',
    padding: 15,
    borderRadius: 4,
    maxWidth: 700,
    fontWeight: theme.fontWeights.medium,
  },
  infoIcon: {
    '& > *': {
      fill: theme.colorPrimaryButtonBackground,
    },
  },
  infoClose: {
    cursor: 'pointer',
  },
  infoDescription: {
    padding: '0px 15px',
  },
  uploaderContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  documentContainerActionWrapper: {
    gridArea: 'documentContainerButton',
    display: 'flex',
    justifySelf: 'flex-end',
    alignItems: 'center',
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 20,
  },
  title: {
    fontSize: theme.fontSizes.medium,
    fontWeight: theme.fontWeights.medium,
  },
  description: {
    display: 'flex',
    marginTop: 10,
  },
  hint: {
    fontSize: theme.fontSizes.smaller,
    color: theme.textLight,
    marginTop: 10,
  },
  uploadLabel: {
    color: theme.primary,
    marginLeft: 5,
  },
  dropZone: {
    flex: 1,
  },
  // FLAG:useDocuAi — styles for the DMS dropzone. They deliberately reproduce the styles in the
  // legacy `DropZoneUploader` (base/components/file-uploader/drop-zone-uploader) one for one because
  // the expense/revenue dropzone must look EXACTLY the same with and without the flag. The legacy
  // component must not be touched for this (flag-OFF must remain byte-identical), so the values are
  // mirrored here — anyone changing them there must also change them here.
  //
  // Previously, the outer element lacked `height: fit-content` and `backgroundColor: #fff`, and the
  // hover/drag colour was `theme.backgroundLight` rather than `#fafbff`: the area could stretch
  // instead of wrapping its content, and it changed to a different shade on hover than without the flag.
  dmsDropZone: {
    composes: ['$dropZone'],
    cursor: 'pointer',
    margin: 0,
    height: 'fit-content',
    width: '100%',
    backgroundColor: '#fff',
  },
  dmsFileInput: {
    cursor: 'pointer',
    margin: 0,
    width: '100%',
    height: 'calc(100% + 4px)',
    borderRadius: 3,
    border: `2px dashed ${theme.borderColor}`,
    '&:hover': {
      borderColor: theme.activeInputBorder,
      background: '#fafbff',
      borderWidth: 2,
    },
    // The drag-over highlight comes from the ONE drop zone above: DocumentsUploadTrigger puts
    // `dropzoneActive` on its own zone (= $dmsDropZone) while a file hovers. This mirrors the legacy
    // DropZoneUploader, which styles `&.dropzoneActive > $fileInput` in exactly the same way — the
    // bordered element is a plain div there too, never a drop zone of its own.
    '$dmsDropZone.dropzoneActive > &': {
      borderColor: theme.activeInputBorder,
      background: '#fafbff',
    },
    '& > input': {
      cursor: 'pointer',
    },
  },
}))

interface ReceiptFileUploaderProps {
  isFileContainerOpen: boolean,
  filesToAssign: any[],
  uploader: any,
  onOpenFileContainer: (event: any) => void
  // FLAG:useDocuAi — when useDmsUpload is true, selected files are handed to onDmsUpload
  // (DMS→DocuAI) instead of the FineUploader. Legacy callers pass neither.
  useDmsUpload?: boolean,
  onDmsUpload?: (files: unknown[]) => void,
}

const ReceiptFileUploader = (props: ReceiptFileUploaderProps) => {
  const classes = useStyles()
  const theme: ThemeInterface = useTheme()
  const dispatch = useDispatch()
  const openUpgradeModal = useUpgradeModal()

  const {
    uploader,
    onOpenFileContainer,
    useDmsUpload,
    onDmsUpload,
  } = props
  const subscriptions = useSelector(state => state.subscription.details.subscriptions)
  const userInfo = useSelector(state => state.user.info)
  const showUploadHint = useSelector(state => state.receipt.edit.showUploadHint)
  const documentSub = useMemo(() => {
    return subscriptions.find((subscription) => subscription.typeOfSubscription === ProductTypes.DOCUMENT)
  }, [subscriptions])
  const accessRights = useMemo(() => {
    return { read: checkAccessRights(USER_RIGHTS.DOCUMENTS.READ, userInfo), write: checkAccessRights(USER_RIGHTS.DOCUMENTS.WRITE, userInfo) }
  }, [userInfo])
  const hasAccess = accessRights?.write || accessRights?.read

  const onOpenContainer = (event) => {
    if (!documentSub && !hasAccess) {
      return openStartTrialModal({
        subscriptionType: ProductTypes.DOCUMENT,
      })
    } else if (!hasAccess && documentSub?.isExpired) {
      return openUpgradeModal({ subscriptionType: ProductTypes.DOCUMENT })
    }

    onOpenFileContainer?.(event)
  }

  const onDropError = (error) => {
    const message = (error === ERROR_REASONS.TOO_MANY_FILES_ERROR)
      ? lang.fileUploaderNoMultipleUploadError
      : lang.defaultErrorMessage

    toast.showToast({ message, type: 'error' })
  }

  const onCloseInfo = () => {
    dispatch(hideUploadHint())
  }

  const fileContainerControl = onOpenFileContainer
    ? (
      <div className={classes.documentContainerActionWrapper}>
        <ReceiptDocumentContainerAction onOpen={onOpenContainer} />
      </div>
    )
    : null

  const description = (
    <>
      <div>{lang.receipt.preview.uploader.description.dragAndDrop}</div>
      <div className={classes.uploadLabel}>{lang.receipt.preview.uploader.description.upload}</div>
    </>
  )

  const info = useMemo(() => {
    if (!showUploadHint) { return null }
    return (
      <div className={classes.info}>
        <SVGIcon className={classes.infoIcon} svg={InfoIcon} height='20px' width='20px' fill={theme.colorPrimaryButtonBackground} />
        <div className={classes.infoDescription}>{lang.receipt.preview.uploader.info}</div>
        <SVGIcon className={classes.infoClose} svg={CloseIcon} height='12px' width='12px' onClick={onCloseInfo} />
      </div>
    )
  }, [showUploadHint])

  const uploadContent = (
    <div className={classes.uploaderWrapper}>
      <div className={classes.iconWrapper}>
        <SVGIcon svg={UploadIcon} height='90px' width='138px' fill={theme.iconFillColor} />
      </div>
      <div className={classes.uploaderContent}>
        <div className={classes.title}>{lang.receipt.preview.uploader.title}</div>
        <div className={classes.description}>{description}</div>
        {/* FLAG:useDocuAi — `useDmsUpload` IS this surface's flag decision (isDmsReceiptUploadEnabled at
            the caller), so the hint is tied to it: with flag-OFF the old sentence remains unchanged.   */}
        <div className={classes.hint}>
          {useDmsUpload ? lang.receipt.preview.uploader.hintDms : lang.receipt.preview.uploader.hint}
        </div>
      </div>
    </div>
  )

  // FLAG:useDocuAi — flag-ON: a DMS→DocuAI dropzone (rpldy) that hands the selected file to
  // onDmsUpload; flag-OFF: the unchanged legacy FineUploader DropZoneUploader (→ /api/file/upload).
  const uploaderControl = useDmsUpload
    ? (
      <DocumentsUploadTrigger
        multiple={false}
        className={classes.dmsDropZone}
        allowedExtensions={RECEIPT_DMS_TRIGGER_ALLOWED_EXTENSIONS}
        onSubmit={onDmsUpload}
      >
        {/* A plain div, NOT a second UploadDropZone: DocumentsUploadTrigger already wraps these
            children in one. rpldy's drop handler calls preventDefault() but NOT stopPropagation(),
            so a drop into a nested zone was handled TWICE — once by the inner zone, then again by
            the trigger's own zone as the event bubbled — and both call upload() on the same Uploady
            context. Two batches, two onSubmit calls, and therefore the same file uploaded to DMS
            twice on every drag & drop. Clicking was never affected: it goes through the single
            hidden file input. The structure now matches the legacy DropZoneUploader one for one —
            one drop zone outside, the dashed bordered element inside. */}
        <div className={classes.dmsFileInput}>
          {uploadContent}
        </div>
      </DocumentsUploadTrigger>
    )
    : (
      <DropZoneUploader
        uploader={uploader}
        multiple={false}
        accept={ALLOWED_FILE_EXTENSIONS}
        className={classes.dropZone}
        onDropError={onDropError}>
        {uploadContent}
      </DropZoneUploader>
    )

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {info}
        {(userInfo.isOwner || hasAccess) && fileContainerControl}
      </div>
      {uploaderControl}
    </div>
  )
}

export default ReceiptFileUploader
